import type { CaseListItem, CaseStatus } from "@/domain/case/case";

/**
 * 「自分が動かさないと進まない」案件だけを抜き出す（issue #29 の確定仕様）。
 *
 * 並びは **承認待ち → 差し戻し → 下書き**。他人を待たせているものから片付けられる
 * ようにする（差し戻しを下書きより先に出しているのと同じ理由）。
 */
const ACTION_REQUIRED_ORDER: readonly CaseStatus[] = ["returned", "draft"];

export type ActionRequiredOptions = {
  /**
   * 自分の番で止まっている案件の id（＝自分宛の承認待ち）。
   *
   * 「関与した案件」に切り替えたときだけ渡す。一覧には自分が承認者の案件も出るので、
   * 要対応がその範囲に追従しないと「一覧には出るのに要対応には出ない」ことになる。
   * 「自分の起票」では渡さない（他人の案件は自分の申請ではない）。
   */
  readonly awaitingMe?: readonly number[];
};

export function selectActionRequired(
  items: readonly CaseListItem[],
  options: ActionRequiredOptions = {},
): readonly CaseListItem[] {
  const awaiting = new Set(options.awaitingMe ?? []);

  const approvals = items.filter(
    (item) => awaiting.has(item.caseId) && !isOwnWork(item),
  );
  const ownWork = ACTION_REQUIRED_ORDER.flatMap((status) =>
    items.filter((item) => item.status === status),
  );

  return [...approvals, ...ownWork];
}

/** 差し戻し・下書きは後続で並べるので、承認待ち側では重複させない。 */
function isOwnWork(item: CaseListItem): boolean {
  return ACTION_REQUIRED_ORDER.includes(item.status);
}
